/**
 * HeaderLeagueSelector Component
 *
 * Compact league selector for the app header.
 * Shows the current league abbreviation with a dropdown to switch leagues.
 * Updates user preferences, which PicksView watches to sync its state.
 */

import { useAuth } from '@contexts/AuthContext';
import { useClickOutside } from '@hooks/useClickOutside';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import {
  getLeagueConfig,
  getLeagueLogoUrl,
  isInSeason,
  type LeagueKey,
  SUPPORTED_LEAGUE_KEYS,
} from '@parlay-picker/shared/league';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useCallback, useRef, useState } from 'react';

export function HeaderLeagueSelector(): ReactElement {
  const { updatePreferences } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, close, isOpen);

  const currentLeagueKey = useCurrentLeagueKey();
  const currentConfig = getLeagueConfig(currentLeagueKey);
  const currentLogoUrl = getLeagueLogoUrl(currentLeagueKey);

  const handleSelect = (key: LeagueKey): void => {
    updatePreferences({ preferredLeague: key });
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-2 bg-theme-surface hover:bg-theme-surface-hover rounded-lg transition-colors text-sm"
      >
        {currentLogoUrl && (
          <img src={currentLogoUrl} alt="" className="w-4 h-4 object-contain" decoding="async" />
        )}
        <span className="font-medium text-theme-text">{currentConfig.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-theme-text-tertiary" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-44 bg-theme-surface rounded-lg shadow-lg py-1 z-20 border border-theme-border max-h-72 overflow-y-auto">
          {[...SUPPORTED_LEAGUE_KEYS]
            .sort((a, b) => {
              const aIn = isInSeason(a);
              const bIn = isInSeason(b);
              if (aIn === bIn) return 0;
              return aIn ? -1 : 1;
            })
            .map((key) => {
              const config = getLeagueConfig(key);
              const logoUrl = getLeagueLogoUrl(key);
              const inSeason = isInSeason(key);
              const isSelected = key === currentLeagueKey;

              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => handleSelect(key)}
                  disabled={!inSeason}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                    !inSeason
                      ? 'text-theme-text-tertiary cursor-not-allowed opacity-50'
                      : isSelected
                        ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                        : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {logoUrl && (
                      <img
                        src={logoUrl}
                        alt=""
                        className="w-4 h-4 object-contain"
                        decoding="async"
                      />
                    )}
                    <span className="font-medium">{config.label}</span>
                  </span>
                  {!inSeason && (
                    <span className="text-[10px] text-theme-text-tertiary">off-season</span>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
